<template>
  <PopoverMenuCard data-test-id="group-select">
    <PopoverMenuSection>
      <ListController @action="dispatchToSelectionList" start-highlight-on-focus-in class="select-popover__search">
        <InputBox v-model="query" focus-on-mount placeholder="Search" icon="24_Search" ref="input">
          <template #icon>
            <SearchIcon16 />
          </template>
        </InputBox>
      </ListController>
    </PopoverMenuSection>
    <PopoverMenuSection>
      <SelectionList
        ref="SelectionList"
        density="dense"
        v-if="projectGroups.length"
        clickable-option="custom-control"
        multi
        v-model="value"
        :options="filteredOptions"
        v-slot="{ label, isSelected, isHighlighted, isDisabled, toggleSelection }"
        max-size="240px"
      >
        <CodeSelectItem
          :is-selected="isSelected"
          :is-disabled="isDisabled"
          :is-highlighted="isHighlighted"
          :primary-text="label.name"
          @toggle="toggleSelection"
        >
          <template #secondary-action>
            <button
              data-test-id="group-select__edit-group-button"
              class="edit-group-cell-renderer__icon-button"
              @click="handleEditSingleGroup(label)"
            >
              <GroupIcon16 />
            </button>
          </template>
        </CodeSelectItem>
      </SelectionList>
    </PopoverMenuSection>
    <PopoverMenuSection divider>
      <PopoverMenuItem
        data-test-id="group-select__manage-groups-button"
        :primary-text="$t('gridList.renderers.cellEditors.groupCellRenderer.manageGroups')"
        @click="handleManageGroupsClick"
      >
        <template #info-icon>
          <SettingsIcon16 />
        </template>
      </PopoverMenuItem>
    </PopoverMenuSection>
  </PopoverMenuCard>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CodeSelectItem from '@components/CodeSelect/CodeSelectItem.vue';
import { filter, toLower } from 'ramda';
import SearchIcon16 from '@components/Icons/SearchIcon16.vue';
import type { ListOptions } from '@components/common/SelectionList.vue';
import SettingsIcon16 from '@components/Icons/SettingsIcon16.vue';
import GroupIcon16 from '@components/Icons/GroupIcon16.vue';
import { ProjectCodeGroup, ProjectMemoGroup, ProjectSourceGroup } from '@/types';

export default defineComponent({
  name: 'GroupSelect',
  components: { SettingsIcon16, SearchIcon16, CodeSelectItem, GroupIcon16 },
  props: {
    entityType: {
      type: String as PropType<'code' | 'memo' | 'source'>,
      required: true,
    },
    modelValue: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    projectGroups: {
      type: Array as PropType<Array<ProjectCodeGroup | ProjectMemoGroup | ProjectSourceGroup>>,
      required: true,
    },
  },
  emits: { 'update:modelValue': null },
  data(): {
    query: string;
  } {
    return {
      query: '',
    };
  },
  methods: {
    dispatchToSelectionList(action) {
      this.$refs.SelectionList?.[action.method](...action.arguments);
    },
    handleManageGroupsClick() {
      this.$store.dispatch('modal/show', { type: this.editGroupModalType, label: 'secondary button' });
    },
    handleEditSingleGroup(group) {
      this.$store.dispatch('modal/show', {
        type: 'editSingleGroup',
        payload: {
          entityType: this.entityType,
          group,
        },
        label: 'secondary button',
      });
    },
  },
  computed: {
    value: {
      get(): Array<string> {
        return this.modelValue;
      },
      set(value: string) {
        this.$emit('update:modelValue', value);
      },
    },
    editGroupModalType(): 'codeEditGroups' | 'memoEditGroups' | 'sourceEditGroups' {
      return `${this.entityType}EditGroups`;
    },
    filteredOptions(): ListOptions {
      if (!this.query) return this.groupOptions;

      return filter(({ label }) => toLower(label.name).indexOf(toLower(this.query)) !== -1, this.groupOptions);
    },
    groupOptions(): ListOptions {
      return this.projectGroups.map((group) => ({
        value: group.id,
        label: {
          name: group.name,
          id: group.id,
          comment: group.comment,
        },
      }));
    },
  },
});
</script>
